import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { useSEO } from '@/hooks/useSEO';
import { comics, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);
  const [showFullDesc, setShowFullDesc] = useState(false);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-book-2-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-600 mb-4">抱歉，您要查看的漫画不存在或已下架</p>
            <Link to="/category/all" className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              浏览其他漫画
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '漫画分类', href: '/category/all' },
    { label: comic.categoryName, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  useSEO({
    title: `${comic.title} - ${comic.categoryName} | 韩漫之家免费在线阅读`,
    description: `${comic.title}：${comic.description}。作者${comic.author}，${comic.status === '连载中' ? '连载至' : '共'}${comic.chapters}话。韩漫之家提供无遮挡下拉式免费在线阅读。`,
    keywords: `${comic.title}, ${comic.categoryName}, 韩漫之家, 免费韩国漫画, 在线阅读, ${comic.author}`,
    canonicalUrl: `http://bferj.cn/comic/${comic.slug}`,
    ogTitle: `${comic.title} - 韩漫之家免费在线阅读`,
    ogDescription: comic.description,
    ogImage: comic.cover,
    ogType: 'article',
    twitterCard: 'summary_large_image',
  });

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://bferj.cn/' },
      { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'http://bferj.cn/category/all' },
      { '@type': 'ListItem', position: 3, name: comic.categoryName, item: `http://bferj.cn/category/${comic.category}` },
      { '@type': 'ListItem', position: 4, name: comic.title },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - ${comic.categoryName}韩国漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-01',
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '韩漫之家',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg data={breadcrumbSchema} />
      <SchemaOrg data={articleSchema} />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Comic Detail */}
          <article className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Cover */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="rounded-lg overflow-hidden bg-background-200 aspect-[3/4] max-w-[300px] mx-auto lg:max-w-full">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩漫之家免费韩国漫画`}
                    title={`${comic.title} - 无遮挡下拉式在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  <Link
                    to={`/comic/${comic.slug}`}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读 <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                </div>
              </div>
            </div>

            {/* Right: Info */}
            <div className="lg:col-span-2">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="flex items-center gap-1 text-sm text-accent-600 font-medium">
                  <i className="ri-star-fill"></i>
                  {comic.rating}
                </span>
                <span className="text-sm text-foreground-500">·</span>
                <span className="text-sm text-foreground-600">{comic.views.toLocaleString()} 次阅读</span>
                <span className="text-sm text-foreground-500">·</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6 p-4 bg-background-100 rounded-lg">
                <div>
                  <span className="text-xs text-foreground-500">作者</span>
                  <p className="text-sm font-medium text-foreground-900">{comic.author}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">画师</span>
                  <p className="text-sm font-medium text-foreground-900">{comic.artist}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">分类</span>
                  <Link to={`/category/${comic.category}`} className="text-sm font-medium text-primary-500 hover:text-primary-600 cursor-pointer">
                    {comic.categoryName}
                  </Link>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">总话数</span>
                  <p className="text-sm font-medium text-foreground-900">{comic.chapters} 话</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">最近更新</span>
                  <p className="text-sm font-medium text-foreground-900">{comic.updatedAt}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mb-8">
                <h2 className="text-base font-heading font-semibold text-foreground-900 mb-3">作品简介</h2>
                <div className="relative">
                  <p className={`text-sm text-foreground-700 leading-relaxed ${!showFullDesc ? 'line-clamp-4' : ''}`}>
                    {comic.description}
                  </p>
                  {comic.description.length > 120 && (
                    <button
                      onClick={() => setShowFullDesc(!showFullDesc)}
                      className="text-sm text-primary-500 hover:text-primary-600 mt-1 cursor-pointer"
                    >
                      {showFullDesc ? '收起' : '展开全部'}
                    </button>
                  )}
                </div>
              </div>

              {/* Chapter List */}
              <div>
                <h2 className="text-base font-heading font-semibold text-foreground-900 mb-3">
                  章节列表（共{comic.chapters}话）
                </h2>
                <div className="flex flex-col gap-1 max-h-[400px] overflow-y-auto pr-1">
                  {Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => (
                    <div
                      key={comic.chapters - i}
                      className="flex items-center justify-between p-3 bg-background-50 border border-background-200/70 rounded-lg hover:bg-primary-50/50 transition-colors cursor-pointer group"
                    >
                      <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors">
                        第 {comic.chapters - i} 话
                      </span>
                      <span className="text-xs text-foreground-500">
                        {i === 0 ? '最新' : ''}
                      </span>
                    </div>
                  ))}
                  {comic.chapters > 20 && (
                    <div className="text-center py-3 text-sm text-foreground-500">
                      仅展示最近20话，更多章节请开始阅读
                    </div>
                  )}
                </div>
              </div>
            </div>
          </article>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="mt-16 mb-8">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop>
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}